import { useCallback, useState } from 'react'
import { useAuth } from '@frontegg/react'
import { LastSeenWindow } from 'models'
import { DEFAULT_LAST_SEEN, LAST_SEEN_OPTIONS, LAST_SEEN_STORAGE_KEY } from '../assetInventory.constants'

const isLastSeenWindow = (value: string | null): value is LastSeenWindow =>
  value !== null && LAST_SEEN_OPTIONS.includes(value as LastSeenWindow)

/**
 * One Last Seen window scopes both tabs, and each user's last choice is remembered.
 * Stored per user so a shared machine does not leak one person's selection to another.
 */
export const useLastSeen = () => {
  const { user } = useAuth()
  const storageKey = `${LAST_SEEN_STORAGE_KEY}.${user?.id ?? 'anonymous'}`

  const [lastSeen, setStoredLastSeen] = useState<LastSeenWindow>(() => {
    try {
      const stored = window.localStorage.getItem(storageKey)

      return isLastSeenWindow(stored) ? stored : DEFAULT_LAST_SEEN
    } catch {
      return DEFAULT_LAST_SEEN
    }
  })

  const setLastSeen = useCallback(
    (value: LastSeenWindow) => {
      setStoredLastSeen(value)

      try {
        window.localStorage.setItem(storageKey, value)
      } catch {
        /* Private browsing and blocked site data still get a working control. */
      }
    },
    [storageKey]
  )

  return { lastSeen, setLastSeen }
}
